import { FC, useCallback, useState } from "react"
import { EItemType } from "src/components/entries/EItemType"
import DeleteItemDialog from "src/components/shared/DeleteItemDialog"
import { IBillV2 } from "src/domain/entities"
import { purchaseBillDeleteService } from "src/domain/services/purchaseBill/purchaseBillDeleteService"
import { saleBillDeleteService } from "src/domain/services/saleBill/saleBillDeleteService"
import { useAuth } from "src/hooks"
import { getErrorMessage } from "src/utils/helpers"

interface Props {
	type: EItemType
	bill: IBillV2
	isOpen: boolean
	setIsOpen: (isOpen: boolean) => void
	onSuccess?: () => void
}

export const BillDeleteDialogController: FC<Props> = ({
	isOpen,
	setIsOpen,
	type,
	bill: { id: billId },
	onSuccess,
}) => {
	const { token } = useAuth()
	const [isLoading, setIsLoading] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)

	const handleBillDelete = useCallback(async () => {
		try {
			setIsLoading(true)
			if (type === EItemType.SALE_BILL) {
				await saleBillDeleteService({ id: billId }, token)
			} else if (type === EItemType.PURCHASE_BILL) {
				await purchaseBillDeleteService({ id: billId }, token)
			} else {
				throw new Error("Invalid item type")
			}

			setIsOpen(false)
			onSuccess?.()
		} catch (err) {
			setErrorMessage(getErrorMessage(err))
		} finally {
			setIsLoading(false)
		}
	}, [setIsOpen, token, onSuccess, billId, type])

	return (
		<DeleteItemDialog
			title={getHeaderLabel(type)}
			isOpen={isOpen}
			onCancel={() => {
				setIsOpen(false)
				setErrorMessage(null)
			}}
			onDelete={handleBillDelete}
			isLoading={isLoading}
			errorMessage={errorMessage}
		/>
	)
}

function getHeaderLabel(type: EItemType) {
	switch (type) {
		case EItemType.SALE_BILL:
			return "Delete Sale Bill"
		case EItemType.PURCHASE_BILL:
			return "Delete Purchase Bill"
	}
	return "Delete Bill"
}
